import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {
  'plot-analysis': '剧情解析',
  'characters': '角色介绍',
  'voice-actors': '声优阵容',
  'ova': 'OVA特辑',
  'iconic-scenes': '名场面',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 font-body">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const categoryName = categoryNameMap[article.category] || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    inLanguage: 'zh-CN',
    url: `https://kissxsister-anime.com/article/${article.slug}`,
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `亲吻姐姐 - ${article.title}`,
    description: article.excerpt,
    url: `https://kissxsister-anime.com/article/${article.slug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: categoryName, path: `/category/${article.category}` },
                { name: article.title },
              ]}
            />
          </div>
        </div>

        <article className="px-4 md:px-6 py-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 text-xs font-body rounded-full bg-primary-100 text-primary-700 mb-4"
              >
                {categoryName}
              </Link>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500 font-body">
                <span>{article.author}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.publishDate}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.readTime}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 text-xs font-body rounded-full bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={`亲吻姐姐 - ${article.title}`}
                title={`亲吻姐姐 - ${article.title}`}
                className="w-full h-auto"
              />
            </div>

            <div
              className="prose prose-lg max-w-none font-body
                prose-headings:font-heading prose-headings:text-foreground-900
                prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-base
                prose-strong:text-foreground-900 prose-strong:font-bold
                [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-base
                [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900
                [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">相关文章推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} article={ra} variant="compact" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}